import clsx from 'clsx';
import type { ChangeEvent, DragEvent, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useId, useRef, useState } from 'react';

import { SDSButton, type SDSButtonProps } from '@repo/sds/SDSButton';
import { BaseFileInput } from '@repo/ui/components/fileInputs/BaseFileInput/BaseFileInput';
import { validateSelectedFile } from '@repo/ui/components/fileInputs/FileInputWithDragAndDrop/validate-selected-file';

import './FileInputWithDragAndDrop.scss';

type FileInputButtonProps = Pick<
  SDSButtonProps,
  'isSubmitting' | 'leftIcon' | 'mode' | 'rightIcon' | 'size' | 'status' | 'text' | 'textType'
>;

interface FileInputProps {
  accept: string;
  inputAriaLabel: string;
  onChange: (file: File) => void | Promise<void>;
  button?: FileInputButtonProps;
  children?: ReactNode;
  dataTestId?: string;
  /** Hidden `<input type="file">` test id for Playwright `setInputFiles`. */
  inputDataTestId?: string;
  className?: string;
  disabledClassName?: string;
  activeClassName?: string;
  isDisabled?: boolean;
  onError?: (message: string) => void;
  maxFileSizeBytes?: number;
  formFieldName?: string;
}

function preventFileDragOver(event: DragEvent<HTMLButtonElement>) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * A file input component with drag and drop support.
 */
export function FileInputWithDragAndDrop({
  accept,
  inputAriaLabel,
  onChange,
  button,
  children,
  dataTestId,
  inputDataTestId,
  className,
  disabledClassName,
  activeClassName,
  isDisabled,
  onError,
  maxFileSizeBytes,
  formFieldName,
}: FileInputProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const isInteractionDisabled = isDisabled || button?.isSubmitting;

  const handleFileChange = async (file: File | undefined) => {
    if (!file || isInteractionDisabled) {
      return;
    }

    if (!validateSelectedFile(file, { accept, maxFileSizeBytes, onError })) {
      return;
    }

    await onChange(file);
  };

  const handleFileInsert = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    await handleFileChange(file);
    // Reset input value to allow selecting the same file again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleFileDrop = async (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);

    if (!isInteractionDisabled) {
      const file = event.dataTransfer.files[0];
      await handleFileChange(file);
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isInteractionDisabled) setIsDragActive(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => {
    if (isInteractionDisabled) return;
    const interactiveAncestor =
      event.target instanceof Element ? event.target.closest('button, input, a, [role="button"]') : null;
    if (event.target !== event.currentTarget && interactiveAncestor && interactiveAncestor !== event.currentTarget) {
      return;
    }
    fileInputRef.current?.click();
  };

  const triggerClassName = clsx(
    'file-input-reset-button',
    className,
    isDragActive && activeClassName,
    isDisabled && disabledClassName,
  );

  return (
    <>
      {button ? (
        <SDSButton
          {...button}
          type="button"
          className={triggerClassName}
          isDisabled={button.isSubmitting ? undefined : isDisabled}
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={preventFileDragOver}
          onDrop={handleFileDrop}
          dataTestId={dataTestId}
          ariaLabel={inputAriaLabel}
        />
      ) : (
        <button
          type="button"
          disabled={isDisabled}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={preventFileDragOver}
          onDrop={handleFileDrop}
          onClick={handleClick}
          data-testid={dataTestId}
          data-form-field-name={formFieldName}
          aria-label={inputAriaLabel}
          className={triggerClassName}
        >
          {children}
        </button>
      )}
      <BaseFileInput
        id={inputId}
        accept={accept}
        onChange={handleFileInsert}
        fileRef={fileInputRef}
        ariaLabel={inputAriaLabel}
        disabled={isInteractionDisabled}
        dataTestId={inputDataTestId}
      />
    </>
  );
}
